import  { useEffect, useRef, useState, useCallback } from 'react'
import { SurveyEngine_Models_BaseResponse, SurveyEngine_Models_MultiResponseResponse, SurveyEngine_Models_WebLayout } from '../../../api'
import { Checkbox, CheckboxHandle} from "@progress/kendo-react-inputs";
import { MultiResponseAnswerData } from '../../../Common/Commands'
import { Label, Error, Hint } from "@progress/kendo-react-labels";
import parse from 'html-react-parser';
import { useGlobalCtx } from '../../../Context/GlobalContext';
import { produce } from 'immer';
import { Button } from '@progress/kendo-react-buttons';
import useCheckMobileScreen from '../../../Hooks/useCheckMobileScreen';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation,faLightbulbOn} from '@fortawesome/pro-regular-svg-icons';

interface MultiResponseProps{
    id: number,
    question: string,
    answers: MultiResponseAnswerData[],
    exclusiveId: any;
    layout: string,
    columns: number,
    minAnswers: number,
    maxAnswers: number,
    isError: boolean,
    focus:boolean
    callback: (response: SurveyEngine_Models_BaseResponse) => void
}

export const MultiResponse = (props: MultiResponseProps) => {
  const labelId = `${props.id}_label`;
  const errorId = `${props.id}_error`;
  const editorId = `${props.id}_editor`;
  const checkboxRefs = useRef<CheckboxHandle[]>([]);
  const buttonRefs = useRef<Button[]>([]);
  const exclusiveId = props.exclusiveId;
  const [touched, setTouched] = useState(false);
  const [isError, setIsError] = useState(props.isError);
  const globalCTX = useGlobalCtx();
  const [answerData] = useState(props.answers.map(item => ({...item, label: parse(item.label!.replace(/""/g,'"'))})))
  const [selectedIds, setSelectedIds] = useState<number[]>(
    answerData.filter((answer) => answer.isSelected).map((item) => item.value));
  const {isMobile} = useCheckMobileScreen()
  const cols: number = props.columns !== undefined ? props.columns : 1 ;
  const displayCols: number = cols > 5 ? 5 : cols;

  const updateResponse = useCallback((selectedIds : number[]) => {
    let response: SurveyEngine_Models_MultiResponseResponse = {
      commandType: "MultiResponse",
      commandIndex: props.id,
      answerIds: selectedIds,
    };
    props.callback(response);
  }, [props]);

  useEffect(() => {
    if (props.focus) {
      if (props.layout === SurveyEngine_Models_WebLayout.CHECK_BOXES) {
        var selectedCheckboxRefs = checkboxRefs.current.filter((ref) => ref != null && ref.element?.checked);
        if ((selectedCheckboxRefs.length <= 0 ) && !touched) {
          checkboxRefs.current[0].element?.focus();
        }
      }
      else if (props.layout === SurveyEngine_Models_WebLayout.SELECTION_BOXES) {
         var selectedButtonRefs = buttonRefs.current.filter((ref) => ref != null && ref.selected);

        if ((selectedButtonRefs.length <= 0 ) && !touched) {
          buttonRefs.current[0]?.element?.focus();
        }
      }
    }
    //update response if do not need require to answer anything (or)
    
    if(props.minAnswers <= 0){
      updateResponse(selectedIds);
    }

    //Update response object if there is any selected answers in answerData object 
    if (answerData !== null  && answerData.filter((answer) => answer.isSelected).length > 0) {
      updateResponse(selectedIds)
    }

   if(props.isError)
   {
      if(selectedIds.length >= props.minAnswers && selectedIds.length <= props.maxAnswers)
      {
        setIsError(false);
      }
      else
      {
        setIsError(true);
      }
   }
    
  }, [props, selectedIds, updateResponse]);

  const handleChange = (value: number) => {
    setTouched(true);
    removeResponse();
    const selected = selectedIds.filter((id) => id === value).length;
    let ids :number[];
    if (selected <= 0) {
      ids = produce(selectedIds, draft => {
        if (exclusiveId) {
          if (value === exclusiveId) {
            draft.length = 0;
          } else {
            var index = draft.findIndex(id => id === exclusiveId);
            if (index !== -1) draft.splice(index, 1)
          }
        }
        draft.push(value);
      });
    }
    else {
      ids = produce(selectedIds, draft => {
        if (exclusiveId && value === exclusiveId) {
          draft.length = 0;
          draft.push(value);
          return;
        }
        var index = draft.findIndex(id => id === value);
        if (index !== -1) draft.splice(index, 1);
      });
      
    }
    setSelectedIds(ids);
    
    if (ids.length < props.minAnswers || ids.length > props.maxAnswers) {
      setIsError(true);
    }
    else{
      updateResponse(ids);
      setIsError(false);
    }
  };
  
  const removeResponse = () => {
    let response: SurveyEngine_Models_MultiResponseResponse = {
      commandType: "Remove",
      commandIndex: props.id,
      answerIds: [],
    };
    props.callback(response);
  }
  
  const HintOrError = () => {
    if (isError ) {
      return <Error
        direction='start'
        aria-describedby={errorId}
        id={errorId}>
        <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />{globalCTX.LangResources.multiResponseMsg.error.replace('{min}', props.minAnswers).replace('{max}', props.maxAnswers)}
      </Error>
    }
    else{
      return <Hint className="theme-validator-hint-text font-semibold" direction='start' id={errorId} aria-describedby={errorId}>
        <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{globalCTX.LangResources.multiResponseMsg.hint.replace('{min}', props.minAnswers).replace('{max}', props.maxAnswers)}</Hint>
    }
  }
  const checkboxInput = () => {
    return (answerData.map((item, index) => {
      return (
        <div key={`CheckboxContainer_${item.value}`} className="theme-multiresponse py-2">
          {
            <Checkbox tabIndex={0}
              id={`mr_${props.id}_${index}`}
              aria-describedby={labelId}
              // label={item.label as string }
              key={`Checkbox_${item.value}`}
              value={selectedIds.filter((option) => option === item.value).length > 0}
              onChange={(e) => { handleChange(item.value) }}
              ref={(ref: CheckboxHandle) => { checkboxRefs.current.push(ref) }}>
                <label className="font-semibold ps-1.5" htmlFor={`mr_${props.id}_${index}`}>{item.label as string }</label>
            </Checkbox>
          }
        </div>
      );
    }))
  }

  const selectionboxInput = () => {
   return (answerData.map((item, index) => {
      return (
        <div key={`SelectionButton_${item.value}`} className='theme-multiresponse flex flex-col space-y-4'>
          {
            <Button
              key={`SelectionButton_${item.value}`}
              ref={(ref: Button) => { buttonRefs.current[index] = ref }}
              togglable={true}
              selected={selectedIds.filter((option) => option === item.value).length > 0}
              aria-describedby={labelId}
              // className="theme-select-box mb-1"
              className={selectedIds.filter((option) => option === item.value).length > 0 ? 'theme-btn-select-box theme-btn-select-box-selected font-bold' : 'theme-btn-select-box font-bold'}
              onClick={() => { handleChange(item.value) }} >
              {item.label}
            </Button>
          }
        </div>
      )
    }))
  }
  return (
    <div className='theme-multi-response'>
      <Label editorRef={checkboxRefs}
        className="theme-question text-xl font-semibold" 
        id={labelId} editorId={editorId}
      >
        {parse(props.question.replace(/""/g,'"'))}
      </Label>
      {isMobile ?
        <div className='theme-answer pt-4 pb-2 flex flex-col space-y-4'>
          {props.layout === SurveyEngine_Models_WebLayout.CHECK_BOXES &&
            checkboxInput()
          }
          {props.layout === SurveyEngine_Models_WebLayout.SELECTION_BOXES &&
            selectionboxInput()
          }
        </div> :
        <div className={'theme-answer pt-4 pb-2 gap-y-2.5 gap-x-4 grid grid-cols-' + displayCols}>
          {props.layout === SurveyEngine_Models_WebLayout.CHECK_BOXES &&
            checkboxInput()
          }
          {props.layout === SurveyEngine_Models_WebLayout.SELECTION_BOXES &&
            selectionboxInput()
          }
        </div>
      }
      <>{HintOrError()}</>
    </div>
  );
}
